import { useCallback, useContext, useMemo } from 'react';

import { AccessContext } from './AccessContext';
import type { AccessContextValue } from './AccessContext';
import { decide, includesPlant, plantsWhere } from './domain/decide';
import type { Decision, PlantsWhere } from './domain/decide';
import { anyPlant, atPlant } from './domain/rules';
import type { AccessRule, Scope } from './domain/rules';
import type { AccessPolicy } from './policy';
import { describeRule, describeScope, reportAccess } from './reporting';
import type { MeAccess } from './types';

const SHOWN_WHILE_REPORTING: Decision = { allow: true, via: 'UNVERIFIED' };

const EVERY_PLANT: PlantsWhere = { kind: 'all', except: [] };

export const useAccessContext = (): AccessContextValue => {
  const context = useContext(AccessContext);
  if (context === null) throw new Error('Access is read inside AccessProvider, once /me has answered');
  return context;
};

export const useAccess = (): MeAccess => useAccessContext().me;

const applyPolicy = (policy: AccessPolicy, rule: AccessRule, scope: Scope, decision: Decision): Decision => {
  if (decision.allow || policy === 'enforce') return decision;
  reportAccess({ kind: 'would-deny', rule: describeRule(rule), scope: describeScope(scope), reason: decision.reason });
  return SHOWN_WHILE_REPORTING;
};

export const useDecision = (rule: AccessRule, scope: Scope): Decision => {
  const { me, policy } = useAccessContext();
  return applyPolicy(policy, rule, scope, decide(me, rule, scope));
};

export const useCan = (rule: AccessRule, scope: Scope): boolean => useDecision(rule, scope).allow;

export const useAccessCheck = (): ((rule: AccessRule, scope: Scope) => boolean) => {
  const { me, policy } = useAccessContext();
  return useCallback((rule: AccessRule, scope: Scope) => applyPolicy(policy, rule, scope, decide(me, rule, scope)).allow, [me, policy]);
};

export const useEnforcedCan = (rule: AccessRule, scope: Scope): boolean => decide(useAccess(), rule, scope).allow;

export const useEnforcedPlantsWhere = (rule: AccessRule): PlantsWhere => {
  const me = useAccess();
  return useMemo(() => plantsWhere(me, rule), [me, rule]);
};

const reportPlantsLeftOut = (me: MeAccess, rule: AccessRule): void => {
  const anywhere = decide(me, rule, anyPlant);
  if (!anywhere.allow) {
    applyPolicy('report-only', rule, anyPlant, anywhere);
    return;
  }
  me.plantIds.forEach((plantId) => {
    const scope = atPlant(plantId);
    applyPolicy('report-only', rule, scope, decide(me, rule, scope));
  });
};

export const usePlantsWhere = (rule: AccessRule): PlantsWhere => {
  const { me, policy } = useAccessContext();
  return useMemo(() => {
    if (policy === 'enforce') return plantsWhere(me, rule);
    reportPlantsLeftOut(me, rule);
    return EVERY_PLANT;
  }, [me, policy, rule]);
};

const filterOf = (where: PlantsWhere) => (plantId: unknown): boolean => typeof plantId === 'string' && includesPlant(where, plantId);

export const usePlantFilter = (rule: AccessRule): ((plantId: unknown) => boolean) => {
  const where = usePlantsWhere(rule);
  return useMemo(() => filterOf(where), [where]);
};

export const useEnforcedPlantFilter = (rule: AccessRule): ((plantId: unknown) => boolean) => {
  const where = useEnforcedPlantsWhere(rule);
  return useMemo(() => filterOf(where), [where]);
};
